import { ReactElement, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Row, Col, Button, Layout, Input, Skeleton, Avatar, notification, Select, message } from "antd";
import { Paper } from "@material-ui/core";
import firebase from "firebase/app";
import { FETCH_ALL_CLUBS_REQUEST } from "../../redux/constants";
import { getAuthState, getClubState } from "../../redux/selectors";
import { BreadCrumbWrapper } from "../../components/GlobalStyles";
import { countries, clubStatus } from "../../constants";

const { Content } = Layout;
const { Option } = Select;

export function ClubsForUser(): ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { clubs } = useSelector(getClubState);
  const { currentUser } = useSelector(getAuthState);
  const [sortedClubs, setSortedClubs] = useState<any[]>([]);
  const [searchKey, setSearchKey] = useState<string>("");
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    dispatch({
      type: FETCH_ALL_CLUBS_REQUEST,
      payload: { currentUser }
    });
    if (currentUser?.countryCode) {
      const currentCountry = countries.find(c => c.code === currentUser?.countryCode.toLowerCase());
      if (currentCountry) setCountry(currentUser?.countryCode.toLowerCase());
    }
  }, []);

  useEffect(() => {
    let mClubs = [...clubs];
    mClubs = mClubs.filter((c) => c.show === "show");
    mClubs.sort((a, b) => {
      return b.countOfBranches - a.countOfBranches;
    });

    if (searchKey !== "") {
      mClubs = mClubs.filter((c) => c.name.toLowerCase().includes(searchKey.toLowerCase()));
    }
    if (country !== "") {
      mClubs = mClubs.filter(c => c.country === country);
    }

    setSortedClubs(mClubs);
  }, [clubs, searchKey, country])

  const requestAccess = (clubName: string, contactEmail: string) => {
    firebase.firestore().collection("mail").add({
      from: currentUser?.email,
      to: contactEmail,
      message: {
        subject: clubName,
        text: "This is request test email"
      }
    }).then(() => {
      const args = {
        message: t("club.request_access"),
        description:
          `You have successfully requested to ${clubName}`,
        duration: 3,
      };
      notification.success(args);
    }).catch(() => {
      const args = {
        message: t("error.oop"),
        description: t("error.request_error"),
        duration: 3,
      };
      notification.error(args);
    });
  }

  const onClickBranches = (clubId: string) => {
    history.push(`/booking/branches/${clubId}`)
  }
  const onClickRequestAccess = (clubname: string, contactEmail: string) => {
    requestAccess(clubname, contactEmail);
  }

  const renderActions = (club: any) => {
    if (club.countOfBranches === 0 || club.status === "coming_soon") {
      return <Button className="club-for-users-table-action-button" onClick={() => onClickBranches(club.key)}>{t("club.coming_soon")}</Button>;
    }
    if (club.status === "request_access") {
      const priorities = club.priority ? club.priority : [];
      if (priorities.findIndex((p: string) => p === currentUser?.id) > -1) {
        return <Button className="club-for-users-table-action-button" onClick={() => onClickBranches(club.key)}>{t("club.select_for_booking")}</Button>
      }
      return <Button className="club-for-users-table-action-button" onClick={() => onClickRequestAccess(club.name, club.contactEmail)}>{t("club.request_access")}</Button>
    }
    if (club.status === "available")
      return <Button className="club-for-users-table-action-button" onClick={() => onClickBranches(club.key)}>{t("club.select_for_booking")}</Button>
    return null;
  }

  return (
    <Content>
      <BreadCrumbWrapper>
        <Breadcrumb style={{ fontSize: 18 }}>
          <Breadcrumb.Item>{t("court.available_clubs")}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <Paper style={{ paddingTop: 20, paddingBottom: 20 }}>
        <Row gutter={24} style={{ marginBottom: 20 }}>
          <Col span={24} >
            <Input
              placeholder="Search Club"
              className="clubs-for-user-search-input"
              prefix={<SearchOutlined />}
              onChange={(event) => setSearchKey(event.target.value)}
            />
            <Select
              className="location-selection"
              placeholder={t("club.select_country")}
              onSelect={(value) => setCountry(value === undefined ? "" : value.toString())}
              value={country}
            >
              {countries.map((c) => (
                <Option key={c.code} value={c.code}>{c.country}</Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, sm: 16, md: 16, lg: 16 }} >
          <Col span={24}>
            <table style={{ width: "100%" }}>
              <thead className="clubs-for-users-table hide-mobile">
                <tr className="clubs-for-user-table-header-row">
                  <td className="" />
                  <td className="clubs-for-user-table-header-column">{t("club.club")}</td>
                  <td className="clubs-for-user-table-header-column">{t("club.branches")}</td>
                  <td className="clubs-for-user-table-header-column">{t("club.website")}</td>
                  <td className="clubs-for-user-table-header-column">{t("club.contact_email")}</td>
                  <td className="clubs-for-user-table-header-column">{t("club.contact_number")}</td>
                  <td className="clubs-for-user-table-header-column">{t("common.action")}</td>
                </tr>
              </thead>
              <tbody >
                {sortedClubs.map(club => (
                  <tr className="clubs-for-user-table-body-row" key={club.clubId}>
                    <td className="clubs-for-user-table-body-column" style={{ textAlign: "center" }}>
                      <Avatar src={club.photo} size={30} />
                    </td>
                    <td className="clubs-for-user-table-body-column">{club.name}</td>
                    <td className="clubs-for-user-table-body-column hide-mobile">{club.countOfBranches}</td>
                    <td className="clubs-for-user-table-body-column hide-mobile">{club.website}</td>
                    <td className="clubs-for-user-table-body-column hide-mobile">{club.contactEmail}</td>
                    <td className="clubs-for-user-table-body-column hide-mobile">{club.contactPhone}</td>
                    <td className="clubs-for-user-table-body-column">
                      {renderActions(club)}
                    </td>
                  </tr>
                ))}
                {sortedClubs.length === 0 && <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 10 }}>{country ? t("club.no_clubs_in_country") : t("club.undefined_country")}</td>
                </tr>}
              </tbody>
            </table>
          </Col>
        </Row>
      </Paper>
    </Content>
  );
}